import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineArrowLeft, HiOutlinePlay, HiOutlineSpeakerWave, HiOutlineCog6Tooth, HiOutlineArrowsPointingOut, HiOutlineRocketLaunch, HiOutlineChartBar, HiOutlineCurrencyDollar, HiOutlineShieldCheck, HiOutlineCheckCircle, HiOutlineAcademicCap } from 'react-icons/hi2';
import './TutorialVideo.css';

const tutorialData = [
  {
    id: 1,
    title: 'Platform Overview',
    description: 'Complete walkthrough of the Stocks4u platform',
    duration: '08:45',
    progress: 100,
    icon: HiOutlineRocketLaunch,
    category: 'Getting Started',
    color: '#a855f7'
  },
  {
    id: 2,
    title: 'Market Analysis Tools',
    description: 'Learn how to analyze markets like a pro',
    duration: '06:30',
    progress: 75,
    icon: HiOutlineChartBar,
    category: 'Market Analysis',
    color: '#3b82f6'
  },
  {
    id: 3,
    title: 'Placing Your First Trade',
    description: 'Step by step guide to place your first trade',
    duration: '07:20',
    progress: 50,
    icon: HiOutlineCurrencyDollar,
    category: 'Trading',
    color: '#ec4899'
  },
  {
    id: 4,
    title: 'Risk Management',
    description: 'Protect your capital with smart risk management',
    duration: '05:15',
    progress: 25,
    icon: HiOutlineShieldCheck,
    category: 'Trading',
    color: '#10b981'
  },
  {
    id: 5,
    title: 'Sector Rotation Strategy',
    description: 'Master the art of rotating between sectors',
    duration: '09:10',
    progress: 0,
    icon: HiOutlineAcademicCap,
    category: 'Features',
    color: '#f59e0b'
  },
  {
    id: 6,
    title: 'Account & Settings',
    description: 'Customize your trading dashboard preferences',
    duration: '04:00',
    progress: 0,
    icon: HiOutlineCog6Tooth,
    category: 'Account & Settings',
    color: '#6366f1'
  }
];

const categories = ['All Topics', 'Getting Started', 'Market Analysis', 'Trading', 'Features', 'Account & Settings'];

const TutorialVideo = ({ onBack }) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All Topics');
  const [selectedVideo, setSelectedVideo] = useState(tutorialData[0]);

  const filteredTutorials = activeCategory === 'All Topics'
    ? tutorialData
    : tutorialData.filter(t => t.category === activeCategory);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="tv-page">
      {/* Back Button */}
      <button className="tv-back-btn" onClick={handleBack}>
        <HiOutlineArrowLeft /> Back to Dashboard
      </button>

      {/* Hero Section */}
      <section className="tv-hero">
        <div className="tv-hero-left">
          <div className="tv-pill">
            <span className="tv-pill-dot" />
            LEARN & GROW
          </div>
          <h1 className="tv-hero-title">
            Tutorial <span className="tv-gradient-text">Video</span>
          </h1>
          <p className="tv-hero-desc">
            Step by step tutorials to help you understand the platform and start trading smarter.
          </p>

          <div className="tv-features-row">
            <div className="tv-feature-chip">
              <HiOutlineCheckCircle className="tv-chip-icon" />
              <div>
                <span className="tv-chip-title">Easy to Follow</span>
                <span className="tv-chip-sub">Step by Step</span>
              </div>
            </div>
            <div className="tv-feature-chip">
              <HiOutlineCheckCircle className="tv-chip-icon" />
              <div>
                <span className="tv-chip-title">Beginner</span>
                <span className="tv-chip-sub">Friendly</span>
              </div>
            </div>
            <div className="tv-feature-chip">
              <HiOutlineCheckCircle className="tv-chip-icon" />
              <div>
                <span className="tv-chip-title">Build Your</span>
                <span className="tv-chip-sub">Confidence</span>
              </div>
            </div>
          </div>

          <button className="tv-play-main-btn" onClick={() => setSelectedVideo(tutorialData[0])}>
            <HiOutlinePlay className="tv-play-icon" />
            Click here to play tutorial video
          </button>
        </div>

        {/* Video Player Card */}
        <div className="tv-hero-right">
          <div className="tv-player-card">
            <div className="tv-player-header">
              <img src="/images/Final Font Stocks4U1.png" alt="Stocks4U" className="tv-player-logo" />
            </div>
            <div className="tv-player-badge">{selectedVideo.category}</div>
            <h2 className="tv-player-title">
              Complete Platform<br />Walk<span className="tv-gradient-text">through</span>
            </h2>

            {/* Play overlay */}
            <div className="tv-play-overlay">
              <div className="tv-play-circle">
                <HiOutlinePlay className="tv-play-circle-icon" />
              </div>
            </div>

            {/* Player Controls */}
            <div className="tv-player-controls">
              <div className="tv-controls-left">
                <HiOutlinePlay className="tv-control-icon" />
                <span className="tv-time">00:00 / {selectedVideo.duration}</span>
              </div>
              <div className="tv-progress-bar">
                <div className="tv-progress-fill" style={{ width: `${selectedVideo.progress}%` }} />
              </div>
              <div className="tv-controls-right">
                <HiOutlineSpeakerWave className="tv-control-icon" />
                <span className="tv-speed">1x</span>
                <HiOutlineCog6Tooth className="tv-control-icon" />
                <HiOutlineArrowsPointingOut className="tv-control-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Browse Section */}
      <section className="tv-browse">
        <div className="tv-browse-header">
          <div>
            <h2 className="tv-browse-title">Browse Tutorials</h2>
            <p className="tv-browse-sub">Choose a topic to start learning</p>
          </div>
          <div className="tv-category-tabs">
            {categories.map(cat => (
              <button
                key={cat}
                className={`tv-cat-tab ${activeCategory === cat ? 'tv-cat-active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Tutorial Cards Grid */}
        <div className="tv-cards-scroll">
          {filteredTutorials.map(tutorial => {
            const IconComp = tutorial.icon;
            return (
              <div
                key={tutorial.id}
                className={`tv-tut-card ${selectedVideo.id === tutorial.id ? 'tv-tut-card-active' : ''}`}
                onClick={() => setSelectedVideo(tutorial)}
              >
                <div className="tv-tut-card-top">
                  <div className="tv-tut-icon-wrap" style={{ background: `linear-gradient(135deg, ${tutorial.color}22, ${tutorial.color}11)`, borderColor: `${tutorial.color}33` }}>
                    <IconComp style={{ color: tutorial.color }} className="tv-tut-icon" />
                  </div>
                  <span className="tv-tut-duration">{tutorial.duration}</span>
                </div>
                <h3 className="tv-tut-title">{tutorial.title}</h3>
                <p className="tv-tut-desc">{tutorial.description}</p>
                <div className="tv-tut-progress">
                  <div className="tv-tut-progress-bar">
                    <div className="tv-tut-progress-fill" style={{ width: `${tutorial.progress}%`, background: tutorial.color }} />
                  </div>
                  <span className="tv-tut-progress-text">{tutorial.progress}% Completed</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default TutorialVideo;
